// COMPONENTS
"use client";

import React, {
  createContext,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
} from "react";
import { useSearchParams } from "next/navigation";
import { trpc } from "@/services/Trpc/client";
import { useInView } from "react-intersection-observer";
import { twMerge } from "tailwind-merge";
import {
  useMediaQuery,
  useWindowSize,
} from "usehooks-ts";

import TemplateListItem, { TemplateListSkeleton } from "./TemplateListItem";

const TemplateListContext = createContext<
  | {
      liveVideoPreviewData: { id: string; blank: boolean } | undefined;
      setLiveVideoPreviewData: (value: { id: string; blank: boolean } | undefined) => void;
      isSingleColumn: boolean;
    }
  | undefined
>(undefined);

export const useTemplateListContext = () => {
  const ctx = useContext(TemplateListContext);
  if (ctx === undefined) {
    throw new Error(
      "useTempplateListContext must be called from withint TemplateListContext Provider",
    );
  }
  return ctx;
};

const Skeletons = React.forwardRef<HTMLDivElement, { totalItemAmount: number }>(
  function ({ totalItemAmount }, ref) {
    const dims = useWindowSize({ initializeWithValue: false });
    // memoize this its slow
    return React.useMemo(() => {
      if (!dims) return null;
      const { width } = dims;
      if (!width) return;
      // this hack loads the correct amount of skeletons for tailwind breakpoints
      let amount;
      if (width > 1536) {
        amount = 5;
      } else if (width > 1280) {
        amount = 4;
      } else if (width > 1024) {
        amount = 3;
      } else {
        amount = 2;
      }

      if (totalItemAmount > 0) amount += amount - (totalItemAmount % amount);
      else amount *= 2;
      const skeletons = [];
      skeletons.length = amount;
      // this ref forwarding garbage ensures that we can load new templates when the
      // first skeleton comes into view without disrupting the layout with another div
      // this is stupid
      for (let i = 0; i < amount; i++) {
        skeletons.push(
          <TemplateListSkeleton key={i} ref={i === 0 ? ref : undefined} />,
        );
      }
      return skeletons;
    }, [totalItemAmount, ref, dims]);
  },
);

Skeletons.displayName = "Skeletons";

type TemplateListProps = {
  /**
   * Optional default category to filter by. URL `?category=` takes precedence —
   * holiday landing pages pass their canonical tag here as a default so the
   * grid is pre-filtered, while still letting visitors pivot to other
   * categories via the pill rail without leaving the page.
   */
  category?: string;
};

export const TemplateList = ({ category: categoryProp }: TemplateListProps = {}) => {
  // manage search params
  const searchParams = useSearchParams();
  const search = searchParams.get("search") ?? "";
  // URL query param wins over prop so pill clicks on holiday pages actually refilter.
  // Empty-string URL params (`?category=`) are treated as absent so the prop fallback still applies.
  const urlCategory = searchParams.get("category") || undefined;
  const category = urlCategory ?? categoryProp ?? undefined;
  const deferredSearch = useDeferredValue(search);

  const listOrder = useMemo(() => {
    if (category !== undefined || deferredSearch !== "") return "publishedAt";
    else return "relevance";
  }, [category, deferredSearch]);

  // fetch data
  const { hasNextPage, status, data, fetchNextPage, isFetchingNextPage } =
    trpc.cardTemplate.list.useInfiniteQuery(
      {
        limit: 15,
        tags: category ? [category] : undefined,
        textSearch: deferredSearch === "" ? undefined : search,
        visibility: "PUBLISHED",
        orderBy: listOrder,
      },
      {
        getPreviousPageParam: (firstPage) => firstPage.pagination.nextCursor,
        getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
      },
    );

  // handle infinite scroll
  const { ref, inView } = useInView();
  useEffect(() => {
    if (inView && !isFetchingNextPage) {
      fetchNextPage().catch(console.error);
    }
  }, [fetchNextPage, inView, isFetchingNextPage]);

  // handle play only 1 preview at a time - no debounce to prevent race conditions
  const [liveVideoPreviewData, setLiveVideoPreviewData] = React.useState<
    { id: string; blank: boolean } | undefined
  >(undefined);

  // Detect single column mode (< 640px / Tailwind sm breakpoint)
  // Uses useMediaQuery instead of useWindowSize for reliable breakpoint detection
  // useWindowSize had issues with the value getting "stuck" after resize
  const isSingleColumn = useMediaQuery("(max-width: 639px)");

  // Debug logging for responsive mode issues
  // console.log('🔍 TemplateList:', { isSingleColumn });

  const totalItemAmount = data
    ? (data.pages.length - 1) * 15 +
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      data.pages[data.pages.length - 1]!.items.length
    : 0;

  if (status === "error") return;
  if (status === "loading")
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 px-4 pt-2 lg:gap-8 lg:px-8 lg:pt-8 xl:grid-cols-4 2xl:grid-cols-5">
        <Skeletons totalItemAmount={totalItemAmount} />
      </div>
    );

  return (
    <TemplateListContext.Provider
      value={{ liveVideoPreviewData, setLiveVideoPreviewData, isSingleColumn }}
    >
      <React.Fragment>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 px-4 pt-2 lg:gap-8 lg:px-8 lg:pt-8 xl:grid-cols-4 2xl:grid-cols-5">
          {data?.pages.map((page) => (
            <React.Fragment key={`page-${page.pagination.nextCursor}`}>
              {page.items.map((item) => (
                <TemplateListItem template={item} key={`template-${item.id}`} />
              ))}
            </React.Fragment>
          ))}
          {hasNextPage ? (
            <Skeletons totalItemAmount={totalItemAmount} ref={ref} />
          ) : (
            <BlankTemplates more={totalItemAmount > 0} />
          )}
        </div>
      </React.Fragment>
    </TemplateListContext.Provider>
  );
};

const BlankTemplates = ({ more }: { more: boolean }) => {
  const { ref, inView } = useInView();

  const { status, data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.cardTemplate.list.useInfiniteQuery(
      {
        limit: 15,
        tags: ["blank"],
        visibility: "PUBLISHED",
      },
      {
        getPreviousPageParam: (firstPage) => firstPage.pagination.nextCursor,
        getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
      },
    );

  useEffect(() => {
    if (inView && !isFetchingNextPage) {
      fetchNextPage().catch(console.error);
    }
  }, [fetchNextPage, inView, isFetchingNextPage]);

  if (status === "error") return;

  const totalItemAmount = data
    ? (data.pages.length - 1) * 15 +
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      data.pages[data.pages.length - 1]!.items.length
    : 0;
  return (
    <React.Fragment>
      <h3
        className={twMerge(
          "text-bold col-span-full w-full px-2 text-center text-2xl",
          more ? "py-8" : "pb-8",
        )}
      >
        {more
          ? "You've explored all the options! Create something unique with these blank cards."
          : "Looking for something special? Start fresh with a customizable blank card!"}
      </h3>
      {data?.pages.map((page) => (
        <React.Fragment key={`blankpage-${page.pagination.nextCursor}`}>
          {page.items.map((item) => (
            <TemplateListItem
              isBlankListing
              template={item}
              key={`blanktemplate-${item.id}`}
            />
          ))}
        </React.Fragment>
      ))}
      {(status === "loading" || hasNextPage) && (
        <Skeletons totalItemAmount={totalItemAmount} ref={ref} />
      )}
    </React.Fragment>
  );
};

export default TemplateList;
